import React from "react";
import { Stack, Box, Typography } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import SendIcon from "@mui/icons-material/Send";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch } from "../redux/store";
import { sendManualSms } from "../redux/actions/smsOutboxActions";
import { cleanFormData } from "../helpers/formHelpers";
import { smsOutboxActions } from "../redux/slices/smsOutbox";
import { SmsManualRequestDto } from "../shared/dtos/sms/SmsManualRequestDto";

const manualSmsSchema = Yup.object().shape({
  phoneNumber: Yup.string()
    .required("Recipient number is required")
    .matches(/^\+?[0-9]{10,15}$/, "Enter a valid phone number (e.g., +254...)"),
  message: Yup.string()
    .required("Message body is required")
    .max(160, "Single SMS limit is 160 characters"),
});

interface SendManualSmsFormProps {
  loading: boolean;
  error: string | null;
}

const SendManualSmsForm: React.FC<SendManualSmsFormProps> = ({
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();

  const initialValues = {
    phoneNumber: "",
    message: "",
  };

  return (
    <Box sx={{ width: "100%" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={() => dispatch(smsOutboxActions.setErrorSendManual(null))}
      />

      <Formik
        initialValues={initialValues}
        validationSchema={manualSmsSchema}
        onSubmit={(values: SmsManualRequestDto) => {
          const payload = cleanFormData(
            values as unknown as Record<string, unknown>,
          ) as unknown as SmsManualRequestDto;
          dispatch(sendManualSms(payload));
        }}
      >
        {({ dirty, isValid, values }: FormikProps<SmsManualRequestDto>) => (
          <Form>
            <Stack spacing={3}>
              <FormTextField
                name="phoneNumber"
                label="Recipient Phone Number"
                placeholder="+254..."
                helperText="Include country code for international delivery"
              />

              <Box>
                <FormTextField
                  name="message"
                  label="Message Content"
                  multiline
                  rows={4}
                  placeholder="Type your message here..."
                />
                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color:
                        values.message.length > 140
                          ? "error.main"
                          : "text.disabled",
                    }}
                  >
                    {values.message.length} / 160
                  </Typography>
                </Box>
              </Box>

              <SubmitButton
                title="Dispatch SMS"
                loading={loading}
                disabled={!dirty || !isValid || loading}
                startIcon={<SendIcon />}
                fullWidth
                sx={{ py: 1.5, fontWeight: 700 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default SendManualSmsForm;
